// First-touch signup attribution — the shared vocabulary.
//
// Meta ran paid traffic at this tool 17–23 Jul 2026 ($651 / 127 sessions /
// 0 signups) and the `users` table stored nothing about how anyone arrived,
// so tool-signup analysis had to round-trip through HubSpot. This module is
// the one place the field list lives so the three hops — browser capture →
// /auth/google/start query → D1 columns — can't drift apart.
//
// NOT the CRM attribution path. HubSpot already gets first- and last-touch
// from Sked's site-wide GTM tag (id 298, 180-day cookie) as the `sk_first_*`
// / `sk_last_*` property family, and that tag already fires on this tool.
// Nothing here writes to HubSpot, deliberately — two writers would give two
// answers. This is the tool's own local copy, for its own queries.
//
// Deliberately DOM-free: `src/shared` compiles under tsconfig.worker
// (lib: ES2022, no DOM), so the readers here take a plain getter rather
// than a URLSearchParams.

/** Ordered so serialization is stable across client and server. Every
 *  key is also the D1 column name on `users`. */
export const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "landing_path",
  "referrer",
] as const;

export type AttributionParamKey = (typeof ATTRIBUTION_PARAM_KEYS)[number];

/** The UTMs and click IDs: the subset analytics tags read off a page URL.
 *  landing_path and referrer describe the first touch, not the campaign. */
export const CAMPAIGN_PARAM_KEYS: readonly AttributionParamKey[] = ATTRIBUTION_PARAM_KEYS.filter(
  (key) => key !== "landing_path" && key !== "referrer",
);

export interface FirstTouchAttribution extends Partial<Record<AttributionParamKey, string>> {
  /** Epoch ms of the FIRST touch, not of the OAuth callback. Carried
   *  through the auth URL so the timestamp survives the redirect. */
  captured_at: number;
}

export const ATTRIBUTION_CAPTURED_AT_PARAM = "attribution_captured_at";

/** A referrer or utm_content can be arbitrarily long; clamp per value so
 *  one hostile field can't eat the whole URL budget on its own. */
export const MAX_ATTRIBUTION_VALUE_LENGTH = 512;

/** Budget for the assembled `/auth/google/start` path + query. Google's
 *  own limits are far higher, but a bloated redirect is a smell and some
 *  intermediaries truncate — shed the low-value fields instead. */
export const MAX_AUTH_URL_LENGTH = 1500;

/** Shed order when over budget: referrer first (least actionable), then
 *  landing_path. The UTMs and click IDs are the whole point — never dropped. */
const DROP_ORDER: readonly AttributionParamKey[] = ["referrer", "landing_path"];

export function hasAnyAttribution(attribution: FirstTouchAttribution | null): boolean {
  if (!attribution) return false;
  return ATTRIBUTION_PARAM_KEYS.some((key) => Boolean(attribution[key]));
}

/** Read attribution off a query string (server side) or any other
 *  key→value source. Returns null when no attribution field carried a
 *  value — a bare `captured_at` is not attribution. */
export function readAttributionParams(
  get: (key: string) => string | null | undefined,
): FirstTouchAttribution | null {
  const attribution: FirstTouchAttribution = { captured_at: 0 };
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = get(key);
    if (value) attribution[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
  }
  if (!hasAnyAttribution(attribution)) return null;
  const capturedAt = Number.parseInt(get(ATTRIBUTION_CAPTURED_AT_PARAM) ?? "", 10);
  attribution.captured_at = Number.isFinite(capturedAt) && capturedAt > 0 ? capturedAt : Date.now();
  return attribution;
}

/** Append the attribution fields to a URL that ALREADY carries a query
 *  string (every caller today builds on `?return_to=…`), URL-encoded and
 *  trimmed to `maxLength`. Returns the URL untouched when there's nothing
 *  to add. If even the undroppable fields blow the budget we send them
 *  anyway — losing the click ID defeats the whole exercise. */
export function appendAttributionParams(
  url: string,
  attribution: FirstTouchAttribution | null,
  maxLength: number = MAX_AUTH_URL_LENGTH,
): string {
  if (!hasAnyAttribution(attribution) || !attribution) return url;
  const dropped = new Set<AttributionParamKey>();
  for (;;) {
    const candidate = url + serializeAttribution(attribution, dropped);
    if (candidate.length <= maxLength) return candidate;
    const next = DROP_ORDER.find((key) => !dropped.has(key) && attribution[key]);
    if (!next) return candidate;
    dropped.add(next);
  }
}

function serializeAttribution(
  attribution: FirstTouchAttribution,
  dropped: ReadonlySet<AttributionParamKey>,
): string {
  const parts: string[] = [];
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = attribution[key];
    if (!value || dropped.has(key)) continue;
    parts.push(`${key}=${encodeURIComponent(value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH))}`);
  }
  if (parts.length === 0) return "";
  parts.push(`${ATTRIBUTION_CAPTURED_AT_PARAM}=${attribution.captured_at}`);
  return `&${parts.join("&")}`;
}

/** The UTMs and click IDs alone, so a request cannot set landing_path or referrer. */
export function readCampaignParams(
  get: (key: string) => string | null | undefined,
): Partial<Record<AttributionParamKey, string>> | null {
  const found: Partial<Record<AttributionParamKey, string>> = {};
  for (const key of CAMPAIGN_PARAM_KEYS) {
    const value = get(key);
    if (value) found[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
  }
  return Object.keys(found).length > 0 ? found : null;
}

/** The same fields as a query string without the "?", or "" when there are none. */
export function campaignQueryString(get: (key: string) => string | null | undefined): string {
  return Object.entries(readCampaignParams(get) ?? {})
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
}
